/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Galileo Computing VueJS Dashboard
  Author: Ian Kibet
  Author URL: http://ian.galileoc.co.ke
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";

import idb from "./db/idb";
import moment from "moment";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return {
      x: 0,
      y: 0,
    };
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: "/",
          redirect: "/dashboard",
        },
        {
          path: "/index",
          redirect: "/login",
        },
        {
          path: "/index.html",
          redirect: "/login",
        },
        {
          path: "/dashboard",
          name: "dashboard",
          component: () => import("./views/Dashboard.vue"),
          meta: {
            rule: "public",
            authRequired: true,
          },
        },
        /* REPORT */
        {
          path: "/report",
          name: "report",
          component: () => import("./views/pages/reports/report.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Dashboard",
                url: "/",
              },
              {
                title: "Store",
              },
              {
                title: "Report",
                active: true,
              },
            ],
            pageTitle: "Report",
            rule: "admin",
            authRequired: true,
          },
        },
        /* SYSTEM */
        {
          path: "/system/users",
          name: "users",
          component: () => import("./views/pages/system/users/List.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Dashboard",
                url: "/",
              },
              {
                title: "System",
              },
              {
                title: "Users",
                active: true,
              },
            ],
            pageTitle: "Users",
            rule: "admin",
            authRequired: true,
          },
        },
        {
          path: "/system/settings",
          name: "settings",
          component: () => import("./views/pages/system/settings/Settings.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Dashboard",
                url: "/",
              },
              {
                title: "System",
              },
              {
                title: "Settings",
                active: true,
              },
            ],
            pageTitle: "Settings",
            rule: "admin",
            authRequired: true,
          },
        },
        /* TRUCKS */
        {
          path: "/trucks",
          name: "trucks",
          component: () => import("./views/pages/trucks/List.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Dashboard",
                url: "/",
              },
              {
                title: "Trucks",
                active: true,
              },
            ],
            pageTitle: "Trucks",
            rule: "admin",
            authRequired: true,
          },
        },
        /* OPERATORS */
        {
          path: "/operators",
          name: "operators",
          component: () => import("./views/pages/operators/List.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Dashboard",
                url: "/",
              },
              {
                title: "Operators",
                active: true,
              },
            ],
            pageTitle: "Operators",
            rule: "admin",
            authRequired: true,
          },
        },
        /* FACTORY */
        {
          path: "/factory",
          name: "factory",
          component: () => import("./views/pages/factory/List.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Dashboard",
                url: "/",
              },
              {
                title: "Factory",
                active: true,
              },
            ],
            pageTitle: "Factory",
            rule: "admin",
            authRequired: true,
          },
        },
        /* FARMS */
        {
          path: "/farms",
          name: "farms",
          component: () => import("./views/pages/farms/List.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Dashboard",
                url: "/",
              },
              {
                title: "Farms",
                active: true,
              },
            ],
            pageTitle: "Farms",
            rule: "admin",
            authRequired: true,
          },
        },
        {
          path: "/farms/:id",
          name: "view-farm",
          component: () => import("./views/pages/farms/View.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Dashboard",
                url: "/",
              },
              {
                title: "Farms",
                url: "/farms",
              },
              {
                title: "View Farm",
                active: true,
              },
            ],
            pageTitle: "Farm",
            rule: "admin",
            authRequired: true,
          },
        },
        /* HARVEST */
        {
          path: "/harvests",
          name: "harvests",
          component: () => import("./views/pages/harvest/List.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Dashboard",
                url: "/",
              },
              {
                title: "Harvests",
                active: true,
              },
            ],
            pageTitle: "Harvests",
            rule: "fm",
            authRequired: true,
          },
        },
        /* EXPENSES */
        {
          path: "/expenses",
          name: "expenses",
          component: () => import("./views/pages/expenses/List.vue"),
          meta: {
            breadcrumb: [
              {
                title: "Dashboard",
                url: "/",
              },
              {
                title: "Expenses",
                active: true,
              },
            ],
            pageTitle: "Expenses",
            rule: "fm",
            authRequired: true,
          },
        },
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: "",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: "/callback",
          name: "auth-callback",
          component: () => import("@/views/Callback.vue"),
          meta: {
            rule: "public",
          },
        },
        {
          path: "/license",
          name: "license",
          component: () =>
            import("@/views/pages/system/installation/LicenseReEntry.vue"),
          meta: {
            rule: "public",
            authRequired: false,
          },
        },
        {
          path: "/welcome",
          name: "welcome",
          component: () =>
            import("@/views/pages/system/installation/start.vue"),
          meta: {
            rule: "public",
            authRequired: false,
          },
        },
        {
          path: "/install",
          name: "install",
          component: () =>
            import("@/views/pages/system/installation/install.vue"),
          meta: {
            rule: "public",
            authRequired: false,
          },
        },
        {
          path: "/login",
          name: "login",
          component: () => import("@/views/pages/login/Login.vue"),
          meta: {
            rule: "public",
          },
        },
        {
          path: "/generate/:id",
          name: "generate",
          component: () =>
            import("@/views/pages/system/installation/generate.vue"),
          meta: {
            rule: "public",
            authRequired: false,
          },
        },
        {
          path: "/register",
          name: "register",
          component: () => import("@/views/pages/register/Register.vue"),
          meta: {
            rule: "public",
          },
        },
        {
          path: "/pages/forgot-password",
          name: "page-forgot-password",
          component: () => import("@/views/pages/ForgotPassword.vue"),
          meta: {
            rule: "public",
          },
        },
        {
          path: "/pages/lock-screen",
          name: "page-lock-screen",
          component: () => import("@/views/pages/LockScreen.vue"),
          meta: {
            rule: "public",
          },
        },
        {
          path: "/soon",
          name: "soon",
          component: () => import("@/views/pages/ComingSoon.vue"),
          meta: {
            rule: "public",
          },
        },
        {
          path: "/pages/error-404",
          name: "page-error-404",
          component: () => import("@/views/pages/Error404.vue"),
          meta: {
            rule: "public",
          },
        },
        {
          path: "/pages/error-500",
          name: "page-error-500",
          component: () => import("@/views/pages/Error500.vue"),
          meta: {
            rule: "public",
          },
        },
        {
          path: "/not-authorized",
          name: "page-not-authorized",
          component: () => import("@/views/pages/NotAuthorized.vue"),
          meta: {
            rule: "public",
          },
        },
        {
          path: "/pages/maintenance",
          name: "page-maintenance",
          component: () => import("@/views/pages/Maintenance.vue"),
          meta: {
            rule: "public",
          },
        },
      ],
    },
    // Redirect to 404 page, if no match found
    {
      path: "*",
      redirect: "/pages/error-404",
    },
  ],
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

var checkLicense = async (to, from, next) => {
  let activation = await idb.getRecord("activation");
  let installation = await idb.getRecord("installation");

  if (
    to.path === "/welcome" ||
    to.path === "/install" ||
    to.path === "/license" ||
    to.name === "generate" ||
    to.path === "/soon"
  ) {
    return next();
  } else {
    if (!activation && !installation) {
      return router.push({
        name: "welcome",
      });
    }

    if (
      (!activation && !installation.complete) ||
      (installation && !activation) ||
      (!installation && activation)
    ) {
      return router.push({
        name: "install",
      });
    }
    if (
      installation &&
      activation &&
      !activation.active &&
      activation.required
    ) {
      return router.push({
        name: "license",
      });
    }
    if (activation && activation.required) {
      var e = moment(activation.expiry).isBefore(new Date(Date.now()));
      var s = moment(new Date()).isAfter(new Date(activation.start));
      if (e) {
        console.log("License Expired");
        await idb.saveRecord({
          type: "activation",
          active: false,
          license: activation.license,
          required: activation.required,
          start: activation.start,
          expiry: activation.expiry,
          date: new Date(Date.now()),
        });
        return router.push({
          name: "license",
        });
      }

      if (!s) {
        return router.push({
          name: "soon",
        });
      }
    }
  }
};

router.beforeEach(async (to, from, next) => {
  await checkLicense(to, from, next);
  if (to.meta.authRequired) {
    var session = JSON.parse(localStorage.getItem("vue-session-key"));
    if (session == null || session.user == null || session.user._id == null) {
      return router.push({
        name: "login",
        query: {
          to: to.path,
        },
      });
    }
  }

  return next();

  // firebase.auth().onAuthStateChanged(() => {
  //   // get firebase current user
  //   const firebaseCurrentUser = firebase.auth().currentUser;

  //   // if (
  //   //     to.path === "/pages/login" ||
  //   //     to.path === "/pages/forgot-password" ||
  //   //     to.path === "/pages/error-404" ||
  //   //     to.path === "/pages/error-500" ||
  //   //     to.path === "/pages/register" ||
  //   //     to.path === "/callback" ||
  //   //     to.path === "/pages/comingsoon" ||
  //   //     (auth.isAuthenticated() || firebaseCurrentUser)
  //   // ) {
  //   //     return next();
  //   // }

  //   // If auth required, check login. If login fails redirect to login page
  //   if (to.meta.authRequired) {
  //     if (!(auth.isAuthenticated() || firebaseCurrentUser)) {
  //       router.push({
  //         name: "login",
  //         query: {
  //           to: to.path
  //         }
  //       });
  //     }
  //   }

  //   return next();
  //   // Specify the current path as the customState parameter, meaning it
  //   // will be returned to the application after auth
  //   // auth.login({ target: to.path });
  // });
});

export default router;
